import React, {Fragment, ReactNode} from 'react';

import {Link} from 'react-router';

import {useCurrentUserState} from 'app/current-user/api';
import {CollapsibleSection} from 'app/general/widgets/CollapsibleSection';
import {attachAssignerToTaskURL, attachUserToTaskURL} from 'app/urls';
import {gt, ngt} from 'app/utils/i18n';

import {CompactTaskItem} from '../CompactTaskItem';
import {DayGroupedTasksList} from '../DayGroupedTasksList';

import {SlimTaskInstanceSerializedType} from 'sweet-types';

import type {NotificationType} from 'app/notifly/types';
import type {TasksState} from 'app/tasks/slice';

interface Props {
  title: ReactNode;
  additionalTitle: ReactNode;
  showTaskDate: boolean;
  notifications: Record<string, Array<NotificationType>>;
  tasksForYou: Array<SlimTaskInstanceSerializedType>;
  tasksFromYou: Array<SlimTaskInstanceSerializedType>;
  onToggle: (
    subSection: keyof TasksState['dashboard']['mineCollapsed']['today']
  ) => any;
  collapsedState: {
    root: boolean;
    toYou: boolean;
    fromYou: boolean;
  };
  moreUrl: string;
  isOverdueSection?: boolean;
}
export const MyTasksRootSection = ({
  title,
  additionalTitle,
  showTaskDate,
  tasksForYou,
  tasksFromYou,
  onToggle,
  collapsedState,
  notifications,
  moreUrl,
  isOverdueSection = false,
}: Props): JSX.Element => {
  const userUrl = useCurrentUserState().data!.url;

  const hasTasks = tasksForYou.length > 0 || tasksFromYou.length > 0;

  const renderAssignedByYou =
    isOverdueSection || // render both sections for overdue tasks
    tasksFromYou.length > 0; // at some point - you have assigned a task to someone

  return (
    <CollapsibleSection
      title={title}
      additionalTitle={additionalTitle}
      collapsed={collapsedState.root}
      onToggle={() => onToggle('root')}
      content={
        hasTasks || isOverdueSection ? (
          <div>
            <CollapsibleSection
              title={gt('Assigned to you')}
              additionalTitle={
                isOverdueSection
                  ? gt(
                      'Showing the first 10 overdue tasks from the last 60 days'
                    )
                  : renderCompleteCounter(tasksForYou)
              }
              sub={true}
              collapsed={collapsedState.toYou}
              onToggle={() => onToggle('toYou')}
              content={
                <Fragment>
                  <RenderList
                    tasks={tasksForYou}
                    showTaskDate={showTaskDate}
                    notifications={notifications}
                  />
                  <PointToMoreTasksIndicator
                    url={attachUserToTaskURL(moreUrl, userUrl)}
                    show={tasksForYou.length >= 10 || isOverdueSection}
                  />
                </Fragment>
              }
            />
            {renderAssignedByYou ? (
              <CollapsibleSection
                title={gt('Assigned by you')}
                additionalTitle={
                  isOverdueSection
                    ? gt(
                        'Showing the first 10 overdue tasks from the last 60 days'
                      )
                    : renderCompleteCounter(tasksFromYou)
                }
                sub={true}
                collapsed={collapsedState.fromYou}
                onToggle={() => onToggle('fromYou')}
                content={
                  <Fragment>
                    <RenderList
                      tasks={tasksFromYou}
                      showTaskDate={showTaskDate}
                      notifications={notifications}
                    />
                    <PointToMoreTasksIndicator
                      url={attachAssignerToTaskURL(moreUrl, userUrl)}
                      show={tasksFromYou.length >= 10 || isOverdueSection}
                    />
                  </Fragment>
                }
              />
            ) : null}
          </div>
        ) : (
          <RenderList
            tasks={tasksForYou}
            showTaskDate={false}
            notifications={{}}
          />
        )
      }
    />
  );
};

const NoTasksFound = (): JSX.Element => (
  <span className="font-italic text-muted">{gt('No tasks found')}</span>
);

const renderListwithFallback = (
  tasks: Array<any>,
  listNode: JSX.Element
): JSX.Element =>
  tasks.length === 0 ? (
    <li className="row">
      <div className="col-12 border-bottom py-3">
        <NoTasksFound />
      </div>
    </li>
  ) : (
    listNode
  );

const renderCompleteCounter = (
  tasks: Array<SlimTaskInstanceSerializedType>
): ReactNode => {
  const total = tasks.length;
  const completed = tasks.filter((t) => t.completed != null).length;

  if (total === 0) {
    return null;
  }

  return completed === 0
    ? ngt('%d Task', '%d Tasks', total)
    : ngt(
        '%2$d of %1$s task completed',
        '%2$d of %1$d tasks completed',
        total,
        completed
      );
};

type ShowMoreProps = {
  url: string;
  show: boolean;
};

const PointToMoreTasksIndicator = ({url, show}: ShowMoreProps) => {
  if (!show) {
    return null;
  }
  return (
    <li className="d-flex">
      <Link to={url} className="btn btn-link mx-auto">
        {gt('Show more')}
      </Link>
    </li>
  );
};

interface RenderListProps {
  tasks: Array<SlimTaskInstanceSerializedType>;
  showTaskDate: boolean;
  notifications: Record<string, Array<NotificationType>>;
}

const RenderList = ({tasks, showTaskDate, notifications}: RenderListProps) => {
  const userTimezone = useCurrentUserState().data!.timezone;

  return renderListwithFallback(
    tasks,
    <ul className={`${showTaskDate ? 'col-12' : ''} list-unstyled mb-0`}>
      {showTaskDate ? (
        <DayGroupedTasksList
          notifications={notifications}
          tasks={tasks}
          userTimezone={userTimezone}
        />
      ) : (
        tasks.map((task, i) => (
          <li className="col-12 border-bottom" key={i}>
            <CompactTaskItem
              task={task}
              showNowLine={false}
              notifications={notifications[task.url]}
              userTimezone={userTimezone}
            />
          </li>
        ))
      )}
    </ul>
  );
};
